import { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { Switch } from '@/shared/components/ui/switch';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useToast } from '@/shared/components/Toast';
import { useCommunicationAsset, useSaveCommunicationAsset } from '../../hooks/useFpResources';
import { AdvisorEditorSection } from './AdvisorEditorSection';

const URL_REGEX = /^https?:\/\/.+/;

type Props = { fpId: string };

export const CommunicationAssetSection = ({ fpId }: Props) => {
  const { showToast } = useToast();
  const { data, isLoading } = useCommunicationAsset(fpId);
  const save = useSaveCommunicationAsset(fpId);

  const [enabled, setEnabled] = useState(false);
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [urlError, setUrlError] = useState('');

  useEffect(() => {
    if (data === undefined) return;
    setEnabled(data?.enabled ?? false);
    setTitle(data?.title ?? '');
    setUrl(data?.url ?? '');
    setDescription(data?.description ?? '');
  }, [data]);

  const handleSave = async () => {
    if (enabled && !url.trim()) {
      setUrlError('URL is required when the asset is enabled');
      return;
    }
    if (url && !URL_REGEX.test(url)) {
      setUrlError('Must be a valid http or https URL');
      return;
    }
    setUrlError('');

    try {
      await save.mutateAsync({
        enabled,
        title: title.trim() || undefined,
        url: url.trim() || null,
        description: description.trim() || null,
      });
      showToast('Communication Asset saved.', 'success');
    } catch {
      showToast('Failed to save Communication Asset.', 'error');
    }
  };

  return (
    <AdvisorEditorSection title="Communication Asset">
      {isLoading ? (
        <div data-testid="communication-asset-skeleton" className="space-y-3">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-md border border-gray-200 p-4">
            <div>
              <Label htmlFor="asset-enabled">Enabled</Label>
              <p className="text-sm text-gray-500">Show this asset on the FP dashboard</p>
            </div>
            <Switch id="asset-enabled" checked={enabled} onCheckedChange={setEnabled} />
          </div>

          <div className="space-y-1">
            <Label htmlFor="asset-title">Title</Label>
            <Input
              id="asset-title"
              value={title}
              placeholder="Communication Assets"
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="asset-url">URL</Label>
            <Input
              id="asset-url"
              aria-label="URL"
              value={url}
              placeholder="https://example.com/assets"
              onChange={(e) => {
                setUrl(e.target.value);
                setUrlError('');
              }}
            />
            {urlError && <p className="text-sm text-red-600">{urlError}</p>}
          </div>

          <div className="space-y-1">
            <Label htmlFor="asset-description">Description</Label>
            <Textarea
              id="asset-description"
              value={description}
              placeholder="Optional notes shown on the FP dashboard"
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="flex justify-end">
            <Button type="button" onClick={handleSave} disabled={save.isPending}>
              <Save className="mr-2 h-4 w-4" />
              {save.isPending ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      )}
    </AdvisorEditorSection>
  );
};
